// Дашборд: KPI, аналитика (графики) и сводка. Реальные данные через API.
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileText, Building2, Calculator, TrendingUp } from 'lucide-react';
import { contractsApi, objectsApi, estimatesApi, dashboardApi } from '@/api';
import { AppLayout, PageHeader } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { BarChart, DonutChart, ProgressBars } from '@/components/charts/Charts';
import { formatMoney, formatDate, contractStatusColor } from '@/lib/format';
import './dashboard.css';

export function DashboardPage() {
  const navigate = useNavigate();
  const { data: contracts, isLoading: cLoad } = useQuery({
    queryKey: ['contracts', { ordering: '-created_at' }],
    queryFn: () => contractsApi.list({ ordering: '-created_at' }),
  });
  const { data: objects } = useQuery({ queryKey: ['objects'], queryFn: () => objectsApi.list() });
  const { data: estimates } = useQuery({ queryKey: ['estimates'], queryFn: () => estimatesApi.list() });
  const { data: stats } = useQuery({ queryKey: ['dashboard-stats'], queryFn: () => dashboardApi.stats() });

  const inWork = contracts?.results.filter((c) => c.status === 'in_work').length ?? 0;
  const totalValue = contracts?.results.reduce((s, c) => s + parseFloat(c.total_amount || '0'), 0) ?? 0;

  const kpis = [
    { icon: FileText, label: 'Договоров', value: contracts?.count ?? 0, hint: `${inWork} в работе` },
    { icon: Building2, label: 'Объектов МКД', value: objects?.count ?? 0, hint: undefined as string | undefined },
    { icon: Calculator, label: 'Смет', value: estimates?.count ?? 0, hint: undefined as string | undefined },
    { icon: TrendingUp, label: 'Портфель', value: formatMoney(totalValue), hint: undefined as string | undefined },
  ];

  return (
    <AppLayout>
      <PageHeader title="Дашборд" subtitle="Сводка по компании" />
      <div className="kpi-grid">
        {kpis.map((k) => (
          <div key={k.label} className="kpi-card">
            <div className="kpi-card__icon"><k.icon size={20} /></div>
            <div className="kpi-card__value">{k.value}</div>
            <div className="kpi-card__label">{k.label}</div>
            {k.hint && <div className="kpi-card__hint">{k.hint}</div>}
          </div>
        ))}
      </div>

      {/* Аналитика */}
      {stats && (
        <div className="dashboard-charts">
          <div className="panel chart-panel">
            <h3 className="chart-panel__title">График производства</h3>
            <p className="chart-panel__sub">Сумма принятых работ (КС-2) по месяцам</p>
            <BarChart data={stats.production_by_month} />
          </div>

          <div className="panel chart-panel">
            <h3 className="chart-panel__title">Объекты по статусу</h3>
            <DonutChart data={stats.objects_by_status} />
          </div>

          <div className="panel chart-panel">
            <h3 className="chart-panel__title">Освоение по заказчику</h3>
            <p className="chart-panel__sub">Выполнено {stats.finance.execution_percent}% от законтрактованного</p>
            <ProgressBars items={[
              { label: 'Законтрактовано', value: stats.finance.contracted, max: stats.finance.contracted, color: 'blue' },
              { label: 'Выполнено (КС-2)', value: stats.finance.executed, max: stats.finance.contracted, color: 'accent' },
              { label: 'Подписано', value: stats.finance.signed, max: stats.finance.contracted, color: 'green' },
            ]} />
          </div>
        </div>
      )}

      <div className="dashboard-section">
        <div className="dashboard-section__head">
          <h2>Недавние договоры</h2>
          <button className="link-btn" onClick={() => navigate('/contracts')}>Все договоры →</button>
        </div>
        <div className="panel">
          {cLoad ? <Spinner label="Загрузка договоров…" /> : (
            <table className="data-table">
              <thead>
                <tr><th>Номер</th><th>Контрагент</th><th>Объект</th><th>Статус</th><th style={{ textAlign: 'right' }}>Сумма</th></tr>
              </thead>
              <tbody>
                {contracts?.results.slice(0, 6).map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/contracts/${c.id}`)}>
                    <td className="mono">{c.number}</td>
                    <td>{c.counterparty_name}</td>
                    <td>{c.object_address || '—'}</td>
                    <td><StatusBadge label={c.status_display} color={contractStatusColor(c.status)} /></td>
                    <td style={{ textAlign: 'right' }} className="mono">{formatMoney(c.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
